// 团队语言包 —— 统一数据入口
//
// 背景：全项目此前普遍用 `res.result.find(item => item.languageCode != 'zh')`
// 把"团队语言包列表"降维成一个"是否开通多语言"的布尔，丢弃了"到底有哪几种语言"。
// 这导致前端无法支持第三种语言（如日语）——连有哪些语言都没保存。
//
// 本模块把「拉取 → 归一为语言码列表 → 派生 hadLang → 全局持久化列表」收口，
// 既兼容旧的布尔判断，又保留完整语言码列表，为多语言（含第三语言）提供依据。
import { findTeamLanguageBindings } from "@api/lang";
import { normalizeCode, SUPPORTED_CODES } from "@util/langConfig";

// 全局持久化 key：当前团队的语言码列表（如 ["zh","en","ja"]）
export const TEAM_LANG_CODES_KEY = "team_lang_codes";
export const TEAM_LANG_CODES_UPDATED_EVENT = "team-lang-codes-updated";

export const setTeamLangCodes = (codes, teamId, defaultCode) => {
  const nextCodes = Array.from(new Set(codes || []));
  sessionStorage.setItem(TEAM_LANG_CODES_KEY, JSON.stringify(nextCodes));
  window.dispatchEvent(
    new CustomEvent(TEAM_LANG_CODES_UPDATED_EVENT, {
      detail: { teamId, codes: nextCodes, defaultCode },
    })
  );
};

export const isLanguageEnabled = (language) => {
  if (typeof language?.enabled === "boolean") return language.enabled;
  if (typeof language?.enable === "boolean") return language.enable;
  if (typeof language?.open === "boolean") return language.open;
  if (language?.openStatus != null) return Number(language.openStatus) === 1;
  return true;
};

// 后端已按 sort 升序返回绑定；第一个开启项就是团队默认语言。
export const getFirstEnabledLanguage = (bindings = []) =>
  bindings.find(isLanguageEnabled);

// 语言包数组 → 语言码数组（保持后端顺序、去空、去重）
// - status 宽松过滤：仅排除「status 字段存在且 ≠1」的未生效语言包；后端不返回 status 时视为生效，
//   避免字段缺失导致语言全部被过滤。
// - zh 兜底：后端列表若不含 zh，前端补入中文；后端已返回 zh 时严格保留接口顺序。
export const toLangCodes = (packs = []) => {
  const codes = (packs || [])
    .filter(
      (pack) =>
        (pack?.status == null || pack.status === 1) && isLanguageEnabled(pack)
    )
    .map((pack) => pack?.languageCode)
    .filter(Boolean);
  const uniqueCodes = Array.from(new Set(codes));
  return uniqueCodes.includes("zh") ? uniqueCodes : ["zh", ...uniqueCodes];
};

// 是否开通多语言：存在中文以外的语言
// 与旧逻辑 `find(item => item.languageCode != 'zh')` 等价，保证行为不变。
export const hasMultiLang = (packs = []) =>
  toLangCodes(packs).some((code) => code !== "zh");

// 读取已持久化的语言码列表（供 initI18N 动态注册 / 语言切换等全局消费）
export const getTeamLangCodes = () => {
  try {
    return JSON.parse(sessionStorage.getItem(TEAM_LANG_CODES_KEY) || "[]");
  } catch {
    return [];
  }
};

// ── 多语言 ml 对象工具（供各编辑/展示组件复用，支持任意团队语言，不写死中英）──

// 本团队支持的语言码（含中文基准 zh），如 ['zh','en','ja','ko']；空时回落 ['zh','en']
export const teamCodes = () => {
  const c = getTeamLangCodes();
  if (!c.length) return ["zh", "en"];
  const uniqueCodes = [...new Set(c)];
  return uniqueCodes.includes("zh") ? uniqueCodes : ["zh", ...uniqueCodes];
};
// 空多语言槽：为每个团队语言初始化空串
export const emptyMl = () => teamCodes().reduce((o, c) => { o[c] = ""; return o; }, {});
// 用同一个值填充所有团队语言（如文件名，各语言默认相同）
export const mlFill = (value) => teamCodes().reduce((o, c) => { o[c] = value; return o; }, {});
// 复制源 ml 里所有团队语言（zh 缺失时回落 base）
export const copyMl = (src, base) => {
  const out = {};
  teamCodes().forEach((c) => { out[c] = src?.[c] ?? ""; });
  out.zh = src?.zh || base || "";
  return out;
};
// 两个 ml 是否"相同"：任一团队语言两边都有值且相等（去重/匹配用）
export const mlSame = (a, b) => teamCodes().some((c) => a?.[c] && b?.[c] && a[c] === b[c]);
// 拼接所有团队语言的值作为搜索文本
export const mlSearchText = (base, src) => [base, ...teamCodes().map((c) => src?.[c])].filter(Boolean).join(" ");
// 保存校验：所有非中文团队语言是否都已填
export const mlComplete = (ml) => teamCodes().filter((c) => c !== "zh").every((c) => ml?.[c]?.trim());

// 拉取团队语言绑定，归一 + 持久化，返回 { ok, packs, codes, hadLang, defaultLanguage }
// - ok：接口是否成功（沿用各调用点原先的 res.success 分支判断）
// - packs：原始绑定数组（含 languageCode/name/sort 等，供需要对象的调用点使用）
// - codes：语言码列表（完整，不再丢弃）
// - hadLang：是否开通多语言（除中文外是否还有其他语言）
export async function fetchTeamLanguages(teamId) {
  if (!teamId) return { ok: false, packs: [], codes: [], hadLang: false, defaultLanguage: null };
  const res = await findTeamLanguageBindings({ teamId });
  const ok = !!res?.success;
  const packs = ok ? res.result || [] : [];
  const codes = toLangCodes(packs);
  const defaultLanguage = getFirstEnabledLanguage(packs);
  if (ok) setTeamLangCodes(codes, teamId, defaultLanguage?.languageCode);
  return {
    ok,
    packs,
    codes,
    hadLang: codes.some((code) => code !== "zh"),
    defaultLanguage,
  };
}

// 语言码归一：'en-US' / 'en_US' / 'EN' 都归一为 'en'
const toLangCode = (value) =>
  normalizeCode(String(value || "").trim().toLowerCase().split(/[-_,]/)[0]);

// 跳转到他人私域（关注列表、收藏列表等）的产品详情前，推导详情页与接口请求应使用的语言码：
// - 对方私域语言包支持当前语言 → 用当前语言：详情页按当前语言展示，该语言同时作为请求头传给详情接口
// - 不支持 → 回落对方私域默认语言（未开通多语言即中文）
// - 接口异常、缺少团队 id 或语言不在前端支持范围 → 兜底中文
// 返回值会作为详情页链接的 fallbackLng，详情页据此确定 i18n 语言与 Accept-Language 请求头。
export async function resolveTargetTeamLangCode(teamId, currentLanguage) {
  const fallbackCode = "zh";
  if (!teamId) return fallbackCode;

  const languageResult = await fetchTeamLanguages(teamId).catch(() => null);
  if (!languageResult?.ok) return fallbackCode;

  const teamCodes = (languageResult.codes || []).map(toLangCode);
  const currentCode = toLangCode(currentLanguage);
  if (teamCodes.includes(currentCode)) return currentCode;

  const defaultCode = toLangCode(languageResult.defaultLanguage?.languageCode);
  if (SUPPORTED_CODES.includes(defaultCode) && teamCodes.includes(defaultCode)) {
    return defaultCode;
  }
  return fallbackCode;
}
